import React from 'react';
import { BossModeAccoutnsTable } from './data';
import { User } from 'models/User';
import { checkIsUltraAdmin, getUserFullName, normalizeEmail } from 'utils';
import usePastTime from 'hooks/usePastTime';
import { Button } from 'ncoded-component-library';
import TrashIcon from 'icons/Trash.icon';
import { useTranslation } from 'react-i18next';
import { formatPhoneNumber } from 'phoneNumberUtils';
import { AvatarWithInitials } from 'components/AvatarWithInitials';

type CellProps = {
  bossMode: User;
  onRemoveBossMode: (bossMode: User) => void;
};

const NameCell: React.FC<CellProps> = ({ bossMode }) => {
  const { firstName, lastName } = bossMode;

  const { t } = useTranslation();

  const fullName = getUserFullName(firstName, lastName);

  return (
    <div className="name-cell">
      <AvatarWithInitials user={bossMode} />
      <p>{fullName || t('na')}</p>
    </div>
  );
};

const EmailCell: React.FC<CellProps> = ({ bossMode }) => {
  const { email } = bossMode;

  const { t } = useTranslation();

  if (!email) return t('na');

  return <div className="email-cell">{normalizeEmail(email)}</div>;
};

const PhoneCell: React.FC<CellProps> = ({ bossMode }) => {
  const { contactNumber } = bossMode;

  const { t } = useTranslation();

  if (!contactNumber) return t('na');

  return <div className="phone-cell">{formatPhoneNumber(contactNumber)}</div>;
};

const CreatedCell: React.FC<CellProps> = ({ bossMode }) => {
  const { createdAt } = bossMode;

  const { t } = useTranslation();
  const pastTime = usePastTime();

  if (!createdAt) return t('na');

  return <div className="created-cell">{pastTime(createdAt)}</div>;
};

const PromotedCell: React.FC<CellProps> = ({ bossMode }) => {
  const { promotedAt } = bossMode;

  const { t } = useTranslation();
  const pastTime = usePastTime();

  if (!promotedAt) return t('na');

  return <div className="promoted-cell">{pastTime(promotedAt)}</div>;
};

const ManageCell: React.FC<CellProps> = ({ bossMode, onRemoveBossMode }) => {
  const { role } = bossMode;

  const isUltraAdmin = checkIsUltraAdmin(role);

  if (isUltraAdmin) return null;

  return (
    <div className="manage-cell">
      <Button
        variant="icon"
        className="warning-action-button"
        icon={TrashIcon}
        onClick={() => onRemoveBossMode(bossMode)}
      />
    </div>
  );
};

export const BODY_CONTENT_GENERATE: Record<
  keyof BossModeAccoutnsTable,
  (cellProps: CellProps) => React.ReactNode
> = {
  firstName: (cellProps: CellProps) => <NameCell {...cellProps} />,
  email: (cellProps: CellProps) => <EmailCell {...cellProps} />,
  phone: (cellProps: CellProps) => <PhoneCell {...cellProps} />,
  createdAt: (cellProps: CellProps) => <CreatedCell {...cellProps} />,
  promotedAt: (cellProps: CellProps) => <PromotedCell {...cellProps} />,
  manage: (cellProps: CellProps) => <ManageCell {...cellProps} />,
};
